<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import {
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import WhatsAppBubblePreview from 'dashboard/components-next/whatsapp/WhatsAppBubblePreview.vue';

const props = defineProps({
  wizardData: { type: Object, required: true },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const router = useRouter();

const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const inboxOptions = computed(() => mapToOptions(inboxes.value, 'id', 'name'));

const templateOptions = computed(() => {
  if (!props.wizardData.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates.value(
    props.wizardData.inboxId
  );
  return templates.map(tmpl => ({
    value: tmpl.id,
    label: `${tmpl.name} (${tmpl.language || 'en'})`,
    template: tmpl,
  }));
});

const hasInboxButNoTemplates = computed(
  () => !!props.wizardData.inboxId && templateOptions.value.length === 0
);

const templatesRoute = computed(
  () => router.resolve({ name: 'whatsapp_templates_list' })?.href
);

const selectedTemplate = computed(() => {
  if (!props.wizardData.templateId) return null;
  return templateOptions.value.find(
    o => o.value === props.wizardData.templateId
  )?.template;
});

const templateBodyText = computed(() => {
  if (!selectedTemplate.value) return '';
  const body = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BODY
  );
  return body?.text || '';
});

const templateHeader = computed(() =>
  findComponentByType(selectedTemplate.value, COMPONENT_TYPES.HEADER)
);

const templateFooter = computed(() =>
  findComponentByType(selectedTemplate.value, COMPONENT_TYPES.FOOTER)
);

const templateButtons = computed(() => {
  const comp = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BUTTONS
  );
  return comp?.buttons || [];
});

const showErrors = ref(false);

const isValid = computed(
  () =>
    !!props.wizardData.title &&
    !!props.wizardData.inboxId &&
    !!props.wizardData.templateId
);

const validate = () => {
  showErrors.value = true;
  return isValid.value;
};

defineExpose({ validate });

const updateField = (field, value) => {
  emit('update', { [field]: value });
};

watch(
  () => props.wizardData.inboxId,
  (newVal, oldVal) => {
    if (oldVal !== null && newVal !== oldVal) {
      emit('update', {
        templateId: null,
        selectedTemplate: null,
        renderedTemplate: '',
        processedParams: {},
        importId: null,
        csvHeaders: [],
        csvSampleRows: [],
        csvTotalRows: 0,
        autoLabel: null,
        columnMapping: {},
      });
    }
  }
);

watch(selectedTemplate, tmpl => {
  if (tmpl) {
    emit('update', {
      selectedTemplate: tmpl,
      renderedTemplate: templateBodyText.value,
      templateParams: {
        name: tmpl.name,
        namespace: tmpl.namespace,
        category: tmpl.category || 'UTILITY',
        language: tmpl.language || 'en_US',
      },
    });
  }
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.TITLE') }}
    </h3>

    <Input
      :model-value="wizardData.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message-type="showErrors && !wizardData.title ? 'error' : 'info'"
      :message="
        showErrors && !wizardData.title
          ? t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.TITLE_REQUIRED')
          : ''
      "
      @update:model-value="updateField('title', $event)"
    />

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        :model-value="wizardData.inboxId"
        :options="inboxOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :has-error="showErrors && !wizardData.inboxId"
        :message="
          showErrors && !wizardData.inboxId
            ? t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.INBOX_REQUIRED')
            : ''
        "
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        @update:model-value="updateField('inboxId', $event)"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        :model-value="wizardData.templateId"
        :options="templateOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :has-error="showErrors && !wizardData.templateId"
        :message="
          showErrors && !wizardData.templateId
            ? t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.TEMPLATE_REQUIRED')
            : ''
        "
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        @update:model-value="updateField('templateId', $event)"
      />
      <div
        v-if="hasInboxButNoTemplates"
        class="flex items-center gap-2 p-3 rounded-lg bg-n-amber-2 text-n-amber-11"
      >
        <span class="i-lucide-alert-triangle size-4 shrink-0" />
        <span class="text-xs">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.NO_TEMPLATES') }}
          <a
            :href="templatesRoute"
            target="_blank"
            rel="noopener noreferrer"
            class="underline font-medium"
          >
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.MANAGE_TEMPLATES') }}
          </a>
        </span>
      </div>
    </div>

    <!-- Template preview (WhatsApp bubble) -->
    <WhatsAppBubblePreview
      v-if="selectedTemplate"
      :header-type="templateHeader?.format || ''"
      :header-text="templateHeader?.text || ''"
      :body-text="templateBodyText"
      :footer-text="templateFooter?.text || ''"
      :buttons="templateButtons"
    />
  </div>
</template>
